import { Button } from "@babackapp/ui/components/button";
import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";
import { FormField } from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { useTranslation } from "react-i18next";
import { AlertTriangleIcon, Trash2, XIcon } from "lucide-react";
import { useState } from "react";
import { useWatch } from "react-hook-form";
import { PanelContent } from "../../lib/panel/panel-content";
import {
  ARRAY_OPERATORS,
  type ConditionOperator,
  OPERATORS,
} from "./condition-config";
import { useWorkflowsEditorConfig } from "../../context/workflows-editor-config-context";
import { useConditionPanel } from "./use-condition-panel";

type ConditionFieldSelectorProps = {
  index: number;
};

function ConditionFieldSelector({ index }: ConditionFieldSelectorProps) {
  const { t } = useTranslation("trackoffice");
  const { conditionFields } = useWorkflowsEditorConfig();
  const panelData = useConditionPanel();

  if (!panelData) return null;

  const { form, handleFieldChange, getConditionPaths, availableFields, isFieldAvailable } = panelData;
  const { fieldPath } = getConditionPaths(index);

  return (
    <ContextualTipsTrigger
      wrapped
      tips={[
        {
          id: "workflow-condition-field",
          title: t(
            "workflowsEditor.tips.conditionField.title",
            "Field selector",
          ),
          description: t(
            "workflowsEditor.tips.conditionField.description",
            "Select the field from your workflow data that you want to evaluate.",
          ),
        },
      ]}
    >
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs text-muted-foreground">
          {t("workflowsEditor.node.condition.fieldLabel", "Field")}
        </Label>
        <FormField
          control={form.control}
          name={fieldPath}
          render={({ field }) => {
            const selectedField = conditionFields.find(
              (f) => f.value === field.value,
            );
            const isUnavailable = !!field.value && !isFieldAvailable(field.value as string);
            return (
              <>
                <Select
                  value={field.value as string}
                  onValueChange={(newFieldValue) => {
                    field.onChange(newFieldValue);
                    handleFieldChange(index, newFieldValue);
                  }}
                >
                  <SelectTrigger className={`h-8 ${isUnavailable ? "border-destructive" : ""}`}>
                    <SelectValue
                      placeholder={t(
                        "workflowsEditor.node.condition.SelectFieldPlaceholder",
                        "Select a field...",
                      )}
                    >
                      {selectedField && (
                        <div className="flex items-center gap-2">
                          {isUnavailable && <AlertTriangleIcon className="size-3 text-destructive" />}
                          <span>{t(selectedField.label, { defaultValue: selectedField.defaultLabel })}</span>
                          <span className="text-xs text-muted-foreground">
                            {selectedField.value}
                          </span>
                        </div>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {availableFields.map((fieldOption) => (
                      <SelectItem
                        key={fieldOption.value}
                        value={fieldOption.value}
                      >
                        <div className="flex items-center gap-2">
                          <span>{t(fieldOption.label, { defaultValue: fieldOption.defaultLabel })}</span>
                          <span className="text-xs text-muted-foreground">
                            {fieldOption.value}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isUnavailable && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertTriangleIcon className="size-3" />
                    {t(
                      "workflowsEditor.node.condition.fieldUnavailable",
                      "This field is not available for the current trigger type",
                    )}
                  </p>
                )}
              </>
            );
          }}
        />
      </div>
    </ContextualTipsTrigger>
  );
}

type ConditionOperatorSelectorProps = {
  index: number;
};

function ConditionOperatorSelector({ index }: ConditionOperatorSelectorProps) {
  const { t } = useTranslation("trackoffice");
  const panelData = useConditionPanel();

  if (!panelData) return null;

  const { form, getAvailableOperators, handleOperatorChange, getConditionPaths } = panelData;
  const { operatorPath, fieldPath } = getConditionPaths(index);

  return (
    <ContextualTipsTrigger
      wrapped
      tips={[
        {
          id: "workflow-condition-operator",
          title: t(
            "workflowsEditor.tips.conditionOperatorField.title",
            "Operator",
          ),
          description: t(
            "workflowsEditor.tips.conditionOperatorField.description",
            "Choose how you want to compare the field value. Available operators depend on the field type you selected (e.g., equals, contains, greater than, less than).",
          ),
        },
      ]}
    >
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs text-muted-foreground">
          {t("workflowsEditor.node.condition.operatorLabel", "Operator")}
        </Label>
        <FormField
          control={form.control}
          name={operatorPath}
          render={({ field }) => {
            const currentFieldValue = form.watch(fieldPath) as string;
            const filteredOperators = getAvailableOperators(currentFieldValue);

            return (
              <Select
                value={field.value as string}
                onValueChange={(val) => {
                  field.onChange(val);
                  handleOperatorChange(index, val);
                }}
                disabled={!filteredOperators?.length}
              >
                <SelectTrigger className="h-8">
                  <SelectValue
                    placeholder={t(
                      "workflowsEditor.node.condition.selectOperatorPlaceholder",
                      "Select operator",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {filteredOperators.map((op) => (
                    <SelectItem key={op.value} value={op.value}>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground font-mono">
                          {op.symbol}
                        </span>
                        <span>{t(op.label, { defaultValue: op.defaultLabel })}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }}
        />
      </div>
    </ContextualTipsTrigger>
  );
}

type ConditionValueInputProps = {
  index: number;
};

function ArrayValueInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
}) {
  const [inputValue, setInputValue] = useState("");

  const addTag = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (!value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInputValue("");
  };

  const removeTag = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    }
    if (e.key === "Backspace" && inputValue === "" && value.length > 0) {
      removeTag(value.length - 1);
    }
  };

  return (
    <div className="flex flex-wrap gap-1 p-1.5 border rounded-md bg-background min-h-8">
      {value.map((tag, i) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-muted rounded border"
        >
          {tag}
          <button
            type="button"
            onClick={() => removeTag(i)}
            className="hover:text-destructive"
            aria-label={`Remove tag: ${tag}`}
          >
            <XIcon className="size-3" />
          </button>
        </span>
      ))}
      <input
        className="flex-1 min-w-[80px] text-sm outline-none bg-transparent"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={addTag}
        placeholder={value.length === 0 ? placeholder : ""}
      />
    </div>
  );
}

function ConditionValueInput({ index }: ConditionValueInputProps) {
  const { t } = useTranslation("trackoffice");
  const panelData = useConditionPanel();

  if (!panelData) return null;

  const { form, getConditionPaths } = panelData;

  const { operatorPath, valuePath } = getConditionPaths(index);

  const operatorValue = useWatch({
    control: form.control,
    name: operatorPath,
  }) as ConditionOperator;

  const operator = OPERATORS.find((o) => o.value === operatorValue);

  if (operator?.noValue === true) {
    return null;
  }

  const isArrayOp = ARRAY_OPERATORS.includes(operatorValue);

  return (
    <ContextualTipsTrigger
      wrapped
      tips={[
        {
          id: "workflow-condition-value",
          title: t(
            "workflowsEditor.tips.conditionValue.title",
            "Comparison value",
          ),
          description: t(
            "workflowsEditor.tips.conditionValue.description",
            "Enter the value to compare against. This can be a static value or you can use variables from your workflow by referencing them with the appropriate syntax.",
          ),
        },
      ]}
    >
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs text-muted-foreground">
          {t("workflowsEditor.node.condition.valueLabel", "Value")}
        </Label>
        <FormField
          control={form.control}
          name={valuePath}
          render={({ field }) =>
            isArrayOp ? (
              <ArrayValueInput
                value={
                  Array.isArray(field.value)
                    ? (field.value as unknown as string[])
                    : []
                }
                onChange={field.onChange}
                placeholder={t(
                  "workflowsEditor.node.condition.arrayValuePlaceholder",
                  "Type and press Enter",
                )}
              />
            ) : (
              <Input
                placeholder={t(
                  "workflowsEditor.node.condition.valuePlaceholder",
                  "Enter value",
                )}
                value={field.value as string}
                onChange={field.onChange}
                className="h-8"
              />
            )
          }
        />
      </div>
    </ContextualTipsTrigger>
  );
}

export function PanelConditions() {
  const panelData = useConditionPanel();
  const { t } = useTranslation("trackoffice");

  if (!panelData) return null;

  const { conditions, addCondition, removeCondition } = panelData;

  return (
    <PanelContent
      onAddClick={addCondition}
      title={t("workflowsEditor.node.condition.conditionsTitle", "Conditions")}
    >
      <div className="flex flex-col gap-3 max-h-[500px] overflow-y-auto pr-2">
        {conditions?.length === 0 && (
          <p className="text-xs text-muted-foreground">
            {t(
              "workflowsEditor.node.condition.conditionsDescription",
              "No conditions yet. Click the + button to add one.",
            )}
          </p>
        )}

        {conditions?.map((condition, index) => {
          void condition;

          return (
            <div
              key={index}
              className="flex flex-col gap-2 p-3 border rounded-lg bg-muted/30"
            >
              {/* Condition header with remove button */}
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Condition {index + 1}</Label>
                <Button
                  size="icon-xs"
                  variant="ghost"
                  onClick={() => removeCondition(index)}
                  className="h-6 w-6 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>

              <ConditionFieldSelector index={index} />

              <ConditionOperatorSelector index={index} />

              <ConditionValueInput index={index} />
            </div>
          );
        })}
      </div>
    </PanelContent>
  );
}
